/**
 * What each journey step looks like on screen.
 *
 * A catalog, not a list: `GET /onboarding/journeys/{scope}/` decides which steps
 * this person gets and in what order, and the widget draws exactly what comes
 * back. This file only answers "what does `invite_users` look like?" — a step
 * the platform sends that has no entry here is skipped rather than guessed at.
 */
import type { LucideIcon } from 'lucide-react'
import {
  CalendarClock,
  KeyRound,
  LayoutGrid,
  Settings,
  ShieldCheck,
  Tags,
  ThumbsDown,
  UserRound,
  UserX,
  Users,
  Wrench
} from 'lucide-react'

import type { ChecklistDismissReason as DismissReason } from 'src/analytics'
import { ChecklistDismissReason, ChecklistItem } from 'src/analytics'
import { DOCS_LINKS } from 'src/constants'
import { CRM_APP_TYPE } from 'src/features/apps/constants/constants'
import { paths } from 'src/router'

import type { ChecklistCard } from './types'

/** Stand-in art. Each task gets its own illustration later — one line each. */
const PLACEHOLDER_IMAGE = '/images/getting-started/placeholder.svg'

const CARDS: readonly ChecklistCard[] = [
  {
    key: ChecklistItem.INVITE,
    target: { kind: 'menu', menuKey: 'users' },
    icon: Users,
    image: PLACEHOLDER_IMAGE,
    docsUrl: DOCS_LINKS.users
  },
  {
    key: ChecklistItem.ROLES,
    // Roles are configured per application now, not per workspace: the sidebar's
    // own `roles` entry is behind a feature flag and gone in most builds.
    target: { kind: 'app', appType: CRM_APP_TYPE, section: 'roles' },
    icon: Tags,
    image: PLACEHOLDER_IMAGE,
    docsUrl: DOCS_LINKS.rolesPermissions
  },
  {
    key: ChecklistItem.AUTHENTICATION,
    target: { kind: 'menu', menuKey: 'security' },
    icon: KeyRound,
    image: PLACEHOLDER_IMAGE,
    docsUrl: DOCS_LINKS.authentication
  },
  {
    key: ChecklistItem.SETTINGS,
    target: { kind: 'menu', menuKey: 'workspaceSettings' },
    icon: Settings,
    image: PLACEHOLDER_IMAGE,
    docsUrl: DOCS_LINKS.workspaceSettings
  },
  {
    key: ChecklistItem.PROFILE,
    target: { kind: 'account', path: paths.account.profile },
    icon: UserRound,
    image: PLACEHOLDER_IMAGE,
    docsUrl: DOCS_LINKS.accountProfile
  },
  {
    key: ChecklistItem.SECURITY,
    target: { kind: 'account', path: paths.account.security },
    icon: ShieldCheck,
    image: PLACEHOLDER_IMAGE,
    docsUrl: DOCS_LINKS.accountSecurity
  },
  {
    key: ChecklistItem.APPS,
    // Menu-gated even though every member has it: the sidebar is still the one
    // that knows the workspace's app path, and a flag could take it away.
    target: { kind: 'menu', menuKey: 'apps' },
    icon: LayoutGrid,
    image: PLACEHOLDER_IMAGE,
    docsUrl: DOCS_LINKS.applications,
    // Nothing in the API changes when somebody has a look at the apps, so this
    // is the one step whose completion the client has to report.
    closedByOpening: true
  }
]

/** Presentation for a step key, or undefined for one this build cannot draw. */
export const checklistCard = (stepKey: string): ChecklistCard | undefined =>
  CARDS.find((card) => card.key === stepKey)

/** How long "hide for a while" lasts. */
export const SNOOZE_DAYS = 7

export const HOVER_ACTIVATE_DELAY_MS = 25

/**
 * How long the checklist waits before opening itself in a workspace that was
 * just created.
 *
 * Just past the welcome overlay, which holds for 1200ms and leaves over 240
 * (`WorkspaceWelcomeOverlay`). The two are the same arrival, but they must not
 * be on screen together: the panel would open behind a full-screen animation
 * and be half missed.
 */
export const CHECKLIST_INTRO_DELAY_MS = 1600

/**
 * How long the pill wears its border beam after the page loads.
 *
 * Long enough to catch the eye of someone still reading the screen, short enough
 * that a permanent animation is not left running in the corner of every session.
 */
export const BEAM_VISIBLE_MS = 60 * 1000

/**
 * The journey is a handful of enum fields that only change when somebody acts —
 * and every action here writes the fresh copy straight into the cache. One round
 * per session is plenty; re-asking on every sidebar render is not.
 */
export const JOURNEY_STALE_MS = 5 * 60 * 1000

/**
 * Offered in the dismiss modal, in this order: the two answers that say "the
 * checklist is wrong about me" first, then the two that say "not now" / "no".
 */
export const DISMISS_REASONS = [
  ChecklistDismissReason.ALREADY_SET_UP,
  ChecklistDismissReason.NOT_RELEVANT,
  ChecklistDismissReason.LATER,
  ChecklistDismissReason.NOT_INTERESTED
] as const

/** One glyph per answer, so the four options are scannable rather than read. */
export const DISMISS_REASON_ICONS: Record<DismissReason, LucideIcon> = {
  [ChecklistDismissReason.ALREADY_SET_UP]: Wrench,
  [ChecklistDismissReason.NOT_RELEVANT]: UserX,
  [ChecklistDismissReason.LATER]: CalendarClock,
  [ChecklistDismissReason.NOT_INTERESTED]: ThumbsDown
}
